import type { CreateCombinedCheckoutRequest } from "@billiard/contracts";
import {
  isCombinedCheckoutReadModel,
  isCreateCombinedCheckoutRequest,
  isOrderDetailReadModel,
  isTableSnapshotList,
} from "@billiard/contracts";
import type { CombinedCheckoutExpectedResult, CombinedCheckoutResult } from "./api";

export const COMBINED_CHECKOUT_RECOVERY_STORAGE_KEY =
  "billiard.ops-web.combined-checkout-recovery.v1";

export interface CombinedCheckoutRecoveryContext {
  tenantId: string;
  storeId: string;
  actorId: string;
}

export interface CombinedCheckoutCommand {
  request: CreateCombinedCheckoutRequest;
  expected: CombinedCheckoutExpectedResult;
}

export type CombinedCheckoutRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN" | "RETRY_ALLOWED";

export interface CombinedCheckoutRecovery extends CombinedCheckoutRecoveryContext {
  command: CombinedCheckoutCommand;
  state: CombinedCheckoutRecoveryState;
}

export type CombinedCheckoutRecoveryView =
  | { status: "NONE"; canRetry: false }
  | { status: "OWNED"; recovery: CombinedCheckoutRecovery; canRetry: boolean }
  | { status: "CONTEXT_MISMATCH" | "COMMAND_MISMATCH"; canRetry: false }
  | {
      status: "RECOVERY_STORAGE_UNAVAILABLE";
      canRetry: false;
      pendingOutcomeMayExist: boolean;
    };

export type CombinedCheckoutAuthorityResolution =
  | { status: "EXECUTED"; result: CombinedCheckoutResult }
  | { status: "RETRY_ALLOWED"; recovery: CombinedCheckoutRecovery }
  | { status: "FAIL_CLOSED" };

let pendingRecovery: CombinedCheckoutRecovery | null = null;
let dispatchingRecovery: CombinedCheckoutRecovery | null = null;
let hydrated = false;
let recoveryUnavailable = false;
let pendingOutcomeMayExist = false;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(value).length === keys.length && keys.every((key) => Object.hasOwn(value, key))
  );
}

function contextIsValid(value: unknown): value is CombinedCheckoutRecoveryContext {
  return (
    isRecord(value) &&
    typeof value.tenantId === "string" &&
    value.tenantId.length > 0 &&
    typeof value.storeId === "string" &&
    value.storeId.length > 0 &&
    typeof value.actorId === "string" &&
    value.actorId.length > 0
  );
}

function commandIsValid(
  value: unknown,
  context: CombinedCheckoutRecoveryContext,
): value is CombinedCheckoutCommand {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["request", "expected"]) ||
    !isCreateCombinedCheckoutRequest(value.request) ||
    !isRecord(value.expected) ||
    !hasExactKeys(value.expected, ["tenantId", "storeId", "actorId", "baselines"]) ||
    value.expected.tenantId !== context.tenantId ||
    value.expected.storeId !== context.storeId ||
    value.expected.actorId !== context.actorId ||
    !Array.isArray(value.expected.baselines) ||
    value.expected.baselines.length !== value.request.orders.length
  ) {
    return false;
  }
  const baselines = value.expected.baselines;
  const tables = baselines.map((baseline) => (isRecord(baseline) ? baseline.table : null));
  if (!isTableSnapshotList(tables, context.tenantId, context.storeId)) return false;
  return value.request.orders.every((requested) => {
    const baseline = baselines.find(
      (candidate) =>
        isRecord(candidate) &&
        isRecord(candidate.order) &&
        candidate.order.sessionId === requested.sessionId,
    );
    return (
      isRecord(baseline) &&
      hasExactKeys(baseline, ["order", "table", "splitPlan"]) &&
      baseline.splitPlan === null &&
      isOrderDetailReadModel(baseline.order) &&
      baseline.order.id === requested.orderId &&
      baseline.order.orderNumber === requested.orderNumber &&
      baseline.order.tableId === requested.tableId &&
      baseline.order.tableNumber === requested.tableNumber &&
      baseline.order.totalFen === requested.expectedAmountFen &&
      isRecord(baseline.table) &&
      baseline.table.revision === requested.expectedTableRevision
    );
  });
}

function immutableRecovery(
  context: CombinedCheckoutRecoveryContext,
  command: CombinedCheckoutCommand,
  state: CombinedCheckoutRecoveryState,
): CombinedCheckoutRecovery {
  const snapshot = structuredClone({ ...context, command, state }) as CombinedCheckoutRecovery;
  const freezeDeep = (value: unknown): void => {
    if (!value || typeof value !== "object" || Object.isFrozen(value)) return;
    for (const child of Object.values(value)) freezeDeep(child);
    Object.freeze(value);
  };
  freezeDeep(snapshot);
  return snapshot;
}

function storedRecovery(value: unknown): CombinedCheckoutRecovery | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "recovery"]) ||
    value.version !== 1 ||
    !isRecord(value.recovery) ||
    !hasExactKeys(value.recovery, ["tenantId", "storeId", "actorId", "command", "state"])
  ) {
    return null;
  }
  const context = {
    tenantId: value.recovery.tenantId,
    storeId: value.recovery.storeId,
    actorId: value.recovery.actorId,
  };
  if (
    !contextIsValid(context) ||
    !commandIsValid(value.recovery.command, context) ||
    !["CLAIMED", "OUTCOME_UNKNOWN", "RETRY_ALLOWED"].includes(String(value.recovery.state))
  ) {
    return null;
  }
  return immutableRecovery(
    context,
    value.recovery.command,
    value.recovery.state as CombinedCheckoutRecoveryState,
  );
}

function storageAccess(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

function recoveryEquals(left: CombinedCheckoutRecovery, right: CombinedCheckoutRecovery): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function readStored(storage: Storage): CombinedCheckoutRecovery | null {
  const encoded = storage.getItem(COMBINED_CHECKOUT_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : storedRecovery(JSON.parse(encoded) as unknown);
}

function saveStored(recovery: CombinedCheckoutRecovery): boolean {
  if (typeof window === "undefined") return true;
  const storage = storageAccess();
  if (!storage) return false;
  try {
    storage.setItem(
      COMBINED_CHECKOUT_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 1, recovery }),
    );
    const saved = readStored(storage);
    return saved !== null && recoveryEquals(saved, recovery);
  } catch {
    return false;
  }
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  if (typeof window === "undefined") return;
  const storage = storageAccess();
  if (!storage) {
    recoveryUnavailable = true;
    return;
  }
  try {
    const encoded = storage.getItem(COMBINED_CHECKOUT_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    pendingOutcomeMayExist = true;
    const stored = storedRecovery(JSON.parse(encoded) as unknown);
    if (!stored) {
      recoveryUnavailable = true;
      return;
    }
    pendingRecovery =
      stored.state === "CLAIMED"
        ? immutableRecovery(stored, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !saveStored(pendingRecovery)) recoveryUnavailable = true;
  } catch {
    recoveryUnavailable = true;
  }
}

function contextMatches(
  recovery: CombinedCheckoutRecovery,
  context: CombinedCheckoutRecoveryContext,
): boolean {
  return (
    recovery.tenantId === context.tenantId &&
    recovery.storeId === context.storeId &&
    recovery.actorId === context.actorId
  );
}

function unavailableView(): CombinedCheckoutRecoveryView {
  return {
    status: "RECOVERY_STORAGE_UNAVAILABLE",
    canRetry: false,
    pendingOutcomeMayExist,
  };
}

export function combinedCheckoutRecoveryContext(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): CombinedCheckoutRecoveryContext {
  return { tenantId: operator.tenantId, storeId: operator.storeId, actorId: operator.id };
}

export function readCombinedCheckoutRecovery(
  context: CombinedCheckoutRecoveryContext,
): CombinedCheckoutRecoveryView {
  hydrate();
  if (recoveryUnavailable || !contextIsValid(context)) return unavailableView();
  if (!pendingRecovery) return { status: "NONE", canRetry: false };
  if (!contextMatches(pendingRecovery, context)) {
    return { status: "CONTEXT_MISMATCH", canRetry: false };
  }
  return {
    status: "OWNED",
    recovery: pendingRecovery,
    canRetry: pendingRecovery.state === "RETRY_ALLOWED",
  };
}

export function combinedCheckoutRecoveryBlocksTable(table: {
  tenantId: string;
  storeId: string;
  tableId: string;
  sessionId: string | null;
}): boolean {
  hydrate();
  if (recoveryUnavailable) return pendingOutcomeMayExist;
  if (
    !pendingRecovery ||
    pendingRecovery.tenantId !== table.tenantId ||
    pendingRecovery.storeId !== table.storeId
  ) {
    return false;
  }
  return pendingRecovery.command.expected.baselines.some(
    (baseline) =>
      baseline.table.tableId === table.tableId ||
      (table.sessionId !== null && baseline.order.sessionId === table.sessionId),
  );
}

export function claimCombinedCheckoutRecovery(
  context: CombinedCheckoutRecoveryContext,
  command: CombinedCheckoutCommand,
): CombinedCheckoutRecoveryView {
  const current = readCombinedCheckoutRecovery(context);
  if (current.status === "OWNED") {
    return JSON.stringify(current.recovery.command) === JSON.stringify(command)
      ? current
      : { status: "COMMAND_MISMATCH", canRetry: false };
  }
  if (current.status !== "NONE") return current;
  if (!contextIsValid(context) || !commandIsValid(command, context)) {
    recoveryUnavailable = true;
    return unavailableView();
  }
  const recovery = immutableRecovery(context, command, "CLAIMED");
  if (!saveStored(recovery)) {
    recoveryUnavailable = true;
    return unavailableView();
  }
  pendingRecovery = recovery;
  pendingOutcomeMayExist = true;
  return { status: "OWNED", recovery, canRetry: false };
}

function verify(
  recovery: CombinedCheckoutRecovery,
  context: CombinedCheckoutRecoveryContext,
): boolean {
  hydrate();
  if (recoveryUnavailable || pendingRecovery !== recovery || !contextMatches(recovery, context)) {
    return false;
  }
  if (typeof window === "undefined") return true;
  const storage = storageAccess();
  if (!storage) return false;
  try {
    const stored = readStored(storage);
    if (!stored || !recoveryEquals(stored, recovery)) {
      recoveryUnavailable = true;
      return false;
    }
    return true;
  } catch {
    recoveryUnavailable = true;
    return false;
  }
}

export function beginCombinedCheckoutDispatch(
  recovery: CombinedCheckoutRecovery,
  context: CombinedCheckoutRecoveryContext,
): boolean {
  if (dispatchingRecovery || recovery.state === "OUTCOME_UNKNOWN" || !verify(recovery, context)) {
    return false;
  }
  dispatchingRecovery = recovery;
  return true;
}

export function finishCombinedCheckoutDispatch(
  recovery: CombinedCheckoutRecovery,
  context: CombinedCheckoutRecoveryContext,
): boolean {
  if (dispatchingRecovery !== recovery || !contextMatches(recovery, context)) return false;
  dispatchingRecovery = null;
  return true;
}

export function markCombinedCheckoutOutcomeUnknown(
  recovery: CombinedCheckoutRecovery,
  context: CombinedCheckoutRecoveryContext,
): boolean {
  if (!verify(recovery, context)) return false;
  const unknown = immutableRecovery(context, recovery.command, "OUTCOME_UNKNOWN");
  if (!saveStored(unknown)) {
    recoveryUnavailable = true;
    return false;
  }
  pendingRecovery = unknown;
  pendingOutcomeMayExist = true;
  return true;
}

export function clearCombinedCheckoutRecovery(
  recovery: CombinedCheckoutRecovery,
  context: CombinedCheckoutRecoveryContext,
): boolean {
  if (!verify(recovery, context)) return false;
  if (typeof window !== "undefined") {
    const storage = storageAccess();
    if (!storage) return false;
    try {
      storage.removeItem(COMBINED_CHECKOUT_RECOVERY_STORAGE_KEY);
      if (storage.getItem(COMBINED_CHECKOUT_RECOVERY_STORAGE_KEY) !== null) return false;
    } catch {
      recoveryUnavailable = true;
      return false;
    }
  }
  if (dispatchingRecovery === recovery) dispatchingRecovery = null;
  pendingRecovery = null;
  pendingOutcomeMayExist = false;
  return true;
}

function resultMatchesCommand(
  result: CombinedCheckoutResult,
  recovery: CombinedCheckoutRecovery,
): boolean {
  const request = recovery.command.request;
  return (
    isCombinedCheckoutReadModel(result.combinedCheckout) &&
    result.combinedCheckout.createdBy === recovery.actorId &&
    result.combinedCheckout.method === request.method &&
    result.combinedCheckout.totalFen === request.expectedTotalFen &&
    result.combinedCheckout.orders.length === request.orders.length &&
    request.orders.every((requested, index) => {
      const allocation = result.combinedCheckout.orders[index];
      return (
        allocation?.sessionId === requested.sessionId &&
        allocation.orderId === requested.orderId &&
        allocation.orderNumber === requested.orderNumber &&
        allocation.tableId === requested.tableId &&
        allocation.tableNumber === requested.tableNumber &&
        allocation.amountFen === requested.expectedAmountFen
      );
    })
  );
}

export function reconcileCombinedCheckoutAuthority(
  recovery: CombinedCheckoutRecovery,
  context: CombinedCheckoutRecoveryContext,
  result: CombinedCheckoutResult | null,
): CombinedCheckoutAuthorityResolution {
  if (recovery.state !== "OUTCOME_UNKNOWN" || !verify(recovery, context)) {
    return { status: "FAIL_CLOSED" };
  }
  if (result !== null) {
    if (!resultMatchesCommand(result, recovery)) return { status: "FAIL_CLOSED" };
    return clearCombinedCheckoutRecovery(recovery, context)
      ? { status: "EXECUTED", result }
      : { status: "FAIL_CLOSED" };
  }
  const retryable = immutableRecovery(context, recovery.command, "RETRY_ALLOWED");
  if (!saveStored(retryable)) {
    recoveryUnavailable = true;
    return { status: "FAIL_CLOSED" };
  }
  pendingRecovery = retryable;
  return { status: "RETRY_ALLOWED", recovery: retryable };
}

export function resetCombinedCheckoutRecoveryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
  try {
    storageAccess()?.removeItem(COMBINED_CHECKOUT_RECOVERY_STORAGE_KEY);
  } catch {
    // The next test starts with a fresh in-memory fail-closed state.
  }
}

export function resetCombinedCheckoutRecoveryMemoryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
}
